import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";
import Table from "../../../core/common/dataTable/index";
import CommonSelect from "../../../core/common/commonSelect";
import PredefinedDateRanges from "../../../core/common/datePicker";
import TooltipOption from "../../../core/common/tooltipOption";
import NotificationModals from "../notificationModals";
import { formatDateToYMD } from "../../../core/utils/dateFormatter";
import { useNotifications } from "../hooks/useNotifications";
import { NotificationResponseType } from "../types/notificationResponse";

// Filter options
const statusOptions = [
  { value: "", label: "All Status" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
];

const NotificationList = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  // Get current user role
  const userRole = useSelector(selectUserRole);

  const [selectedNotificationId, setSelectedNotificationId] = useState<
    string | null
  >(null);

  // Use the notifications hook
  const {
    notifications,
    paginationData,
    isLoading,
    isError,
    error,
    handlePageChange,
    handlePageSizeChange,
    handleSearch,
    searchTerm,
    refetch,
  } = useNotifications();

  // Ref for the dropdown menu
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const handleDeleteClick = (id: number) => {
    setSelectedNotificationId(id.toString());
  };

  const handleApplyClick = () => {
    // Apply filters here
    refetch();
    // Close dropdown
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  // Handler for Add Notification button
  const handleAddNotificationClick = () => {
    // Route to different component based on user role
    if (userRole === "centeradmin") {
      navigate(routes.addCenterAdminNotification);
    } else {
      navigate(routes.addNotification);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return formatDateToYMD(dateString);
  };

  // Table columns definition
  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1,
      sorter: (a: any, b: any) => a.id - b.id,
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (text: string, record: NotificationResponseType) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link
                to={`${routes.notificationDetail.replace(
                  ":id",
                  record.id.toString()
                )}`}
                className="link-primary"
              >
                {text}
              </Link>
            </p>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.title.localeCompare(b.title),
    },
    // Only show "Created By" column for superadmin
    ...(userRole == "superadmin"
      ? [
          {
            title: "Created By",
            dataIndex: "created_by_username",
            render: (created_by_username: any) => (
              <div>{created_by_username || "—"}</div>
            ),
            sorter: (a: any, b: any) => {
              const nameA = a.created_by_username || "";
              const nameB = b.created_by_username || "";
              return nameA.localeCompare(nameB);
            },
          },
        ]
      : []),
    {
      title: "Attachments",
      dataIndex: "attachments",
      render: (attachments: string[]) => {
        const validAttachments = attachments?.filter((att) => att !== "") || [];
        return (
          <span>
            {validAttachments.length > 0 ? (
              <span className="d-flex align-items-center">
                <i className="ti ti-paperclip me-1"></i>
                {validAttachments.length}
              </span>
            ) : (
              <span className="text-muted d-flex align-items-center">
                <i className="ti ti-file-off me-1"></i>
                None
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: "Published Date",
      dataIndex: "publishedOn",
      render: (date: string, record: NotificationResponseType) => {
        if (!record.isPublished) {
          return (
            <span className="text-muted fst-italic">Not published yet</span>
          );
        }
        return date ? formatDate(date) : "—";
      },
      sorter: (a: any, b: any) => {
        if (!a.publishedOn) return -1;
        if (!b.publishedOn) return 1;
        return (
          new Date(a.publishedOn).getTime() - new Date(b.publishedOn).getTime()
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isPublished",
      render: (isPublished: boolean) => (
        <span
          className={`badge badge-soft-${
            isPublished ? "success" : "warning"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {isPublished ? "Published" : "Draft"}
        </span>
      ),
      sorter: (a: any, b: any) =>
        a.isPublished === b.isPublished ? 0 : a.isPublished ? 1 : -1,
    },
    {
      title: "Action",
      render: (record: NotificationResponseType) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to={`${routes.notificationDetail.replace(
                      ":id",
                      record.id.toString()
                    )}`}
                  >
                    <i className="ti ti-menu me-2" />
                    View Details
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to={
                      userRole === "centeradmin"
                        ? routes.editCenterAdminNotification.replace(
                            ":id",
                            record.id.toString()
                          )
                        : routes.editNotification.replace(
                            ":id",
                            record.id.toString()
                          )
                    }
                  >
                    <i className="ti ti-edit me-2" />
                    Edit
                  </Link>
                </li>
                {!record.isPublished ? (
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      data-bs-toggle="modal"
                      data-bs-target="#publish-modal"
                      onClick={() => handleDeleteClick(record.id)}
                    >
                      <i className="ti ti-send me-2" />
                      Publish Now
                    </Link>
                  </li>
                ) : (
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      data-bs-toggle="modal"
                      data-bs-target="#unpublish-modal"
                      onClick={() => handleDeleteClick(record.id)}
                    >
                      <i className="ti ti-x me-2" />
                      Unpublish
                    </Link>
                  </li>
                )}
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    data-bs-toggle="modal"
                    data-bs-target="#delete-modal"
                    onClick={() => handleDeleteClick(record.id)}
                  >
                    <i className="ti ti-trash me-2" />
                    Delete
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Notifications</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Notifications</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    All Notifications
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* <TooltipOption /> */}
              <div className="mb-2">
                <button
                  onClick={handleAddNotificationClick}
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Notification
                </button>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Notifications List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={statusOptions}
                                defaultValue={statusOptions[0]}
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">
                                Has Attachments
                              </label>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="hasAttachments"
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="hasAttachments"
                                >
                                  Only show notifications with attachments
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link to="#" className="btn btn-light me-3">
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by A-Z
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1 active">
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Oldest First
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div className="text-center">Loading notifications...</div>
              ) : isError ? (
                <div className="alert alert-danger m-3">
                  Error loading notifications: {error?.toString()}
                </div>
              ) : (
                <Table
                  dataSource={notifications}
                  columns={columns}
                  rowKey="id"
                  onSearchChange={handleSearch}
                  pagination={{
                    pageSize: paginationData.pageSize,
                    current: paginationData.currentPage,
                    total: paginationData.totalItems,
                    onChange: handlePageChange,
                    pageSizeOptions: [10, 20, 30],
                  }}
                />
              )}
            </div>
          </div>
          {/* /Notifications List */}
        </div>
      </div>
      {/* /Page Wrapper */}
      <NotificationModals
        NotificationId={selectedNotificationId || undefined}
        onSuccess={() => {
          // Refresh the notifications list
          refetch();
        }}
      />
    </>
  );
};

export default NotificationList;
